import { InputHTMLAttributes, useRef, useState } from 'react';

import clsx from 'clsx';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';

import { useTutorialStore } from '@event/store/useTutorialStore';
import { CreateEvent } from '@event/types';
import FormActions from '@ui/components/FormActions';
import IconLocation from '@ui/components/icons/IconLocation';
import InputWrapper from '@ui/components/InputWrapper';
import {
  onKeyDownChecker,
  onPasteChecker,
} from '@utils/contentEditableChecker';
import useClientStore from '@utils/hooks/useClientStore';

interface LocationProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  classNameWrapper?: string;
  classNameInput?: string;
  id: keyof CreateEvent;
  value: string;
  register: UseFormRegister<CreateEvent>;
  setValue: UseFormSetValue<CreateEvent>;
  isTutorialEventLocation?: boolean;
}

const MAX_LENGTH = 90 as const;

const Location = ({
  placeholder,
  classNameWrapper,
  classNameInput,
  value,
  id,
  register,
  setValue,
  isTutorialEventLocation = false,
}: LocationProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const tutorialStore = useClientStore(useTutorialStore, (state) => state);

  return (
    <>
      <InputWrapper isFocused={isFocused} className={classNameWrapper}>
        <IconLocation
          className={clsx(
            'absolute left-2 top-1/2 -translate-y-1/2 lg:left-7',
            isClicked ? 'fill-brand-gray-100' : 'fill-brand-gray-200',
          )}
        />
        <div
          {...register(id)}
          id={id}
          ref={ref}
          className={clsx(
            'form-input w-full break-words rounded-[8px] border-0 bg-brand-transparent-100 py-4 pl-10 pr-2 font-montserrat text-[16px]/[24px] font-medium text-brand-gray-100 caret-brand-gray-100 transition-all duration-300 selection:bg-brand-primary-10070 empty:before:text-brand-gray-200 empty:before:content-[attr(placeholder)] hover:text-brand-gray-100 focus:bg-brand-primary-300 focus:text-brand-black-100 focus:ring-0 focus:ring-offset-0 active:bg-brand-primary-300 active:text-brand-black-100 motion-reduce:transition-none lg:rounded-[16px] lg:py-6 lg:pl-16 lg:pr-8',
            classNameInput,
            isClicked ? 'text-brand-gray-100' : 'text-brand-gray-200',
          )}
          placeholder={placeholder}
          onClick={() => setIsClicked(true)}
          onKeyDown={(e) => onKeyDownChecker(e, MAX_LENGTH)}
          onPaste={(e) => onPasteChecker(e, MAX_LENGTH)}
          onInput={(e) => {
            const textContent = e.currentTarget.textContent || '';

            setValue(id, textContent.substring(0, MAX_LENGTH), {
              shouldDirty: true,
            });
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 100)}
          contentEditable
          suppressContentEditableWarning
        >
          {value}
        </div>
      </InputWrapper>
      {isFocused && (
        <FormActions
          onSubmit={() => {
            setIsFocused(false);
            ref.current?.blur();

            if (isTutorialEventLocation) {
              tutorialStore?.goToNextStep();
            }
          }}
          onCancel={() => {
            if (ref.current) {
              ref.current.innerHTML = '';
              ref.current.textContent = '';
            }
            setValue(id, '', {
              shouldDirty: true,
            });
            setIsFocused(false);
            ref.current?.blur();
          }}
          className={clsx(
            'right-4 md:right-8 md:top-auto',
            isTutorialEventLocation && 'top-[-60px]',
          )}
        />
      )}
    </>
  );
};

export default Location;
